import { useId, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface TooltipProps {
  /** Tooltip text. Also drives the accessible description. */
  label: string;
  children: ReactNode;
  side?: 'top' | 'bottom';
  className?: string;
}

/**
 * Lightweight tooltip for icon-only controls.
 *
 * Visibility is driven by React state rather than Tailwind `group-hover`
 * variants: a lockfile regression previously stripped those variants from the
 * production build and silently made hover-revealed UI invisible, so hover
 * affordances here must not depend on variant generation.
 *
 * Shows on hover and on keyboard focus, and is exposed to assistive tech via
 * aria-describedby — the trigger keeps its own aria-label as the accessible name.
 */
export function Tooltip({ label, children, side = 'top', className }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const id = useId();

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocusCapture={() => setVisible(true)}
      onBlurCapture={() => setVisible(false)}
    >
      <span aria-describedby={visible ? id : undefined} className="inline-flex">
        {children}
      </span>
      {visible && (
        <span
          id={id}
          role="tooltip"
          className={cn(
            'pointer-events-none absolute left-1/2 z-50 -translate-x-1/2 whitespace-nowrap',
            'rounded-md bg-foreground px-2 py-1 text-xs font-medium text-background shadow-md',
            side === 'top' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            className,
          )}
        >
          {label}
        </span>
      )}
    </span>
  );
}
